import { useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "@/api/client";
import type { AttachmentOut } from "@/api/types";
import { Field, Input, Select, Textarea, tx } from "@/components/ui";
import { usePalette } from "@/components/FilterBar";

type Props = { brandId: string; entity: "appointments" | "customers"; values: Record<string, unknown>; onChange: (v: Record<string, unknown>) => void; className?: string };

/** A custom field of type `file`: upload to /app/attachments, keep the id; shows the file name with a signed link. */
export function FileField({ brandId, entity, value, onChange }: { brandId: string; entity: string; value: string; onChange: (id: string) => void }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const meta = useQuery({ queryKey: ["attachment", value], queryFn: () => api<AttachmentOut>(`/app/attachments/${value}`), enabled: !!value, retry: false });
  const up = useMutation({
    mutationFn: async (file: File) => { const fd = new FormData(); fd.append("brand_id", brandId); fd.append("entity", entity); fd.append("file", file); return api<AttachmentOut>("/app/attachments", { method: "POST", body: fd }); },
    onSuccess: (a) => { setError(null); onChange(a.id); }, onError: (e) => setError(e instanceof ApiError ? e.detail : t("app.error")),
  });
  return (
    <div className="flex min-h-9 flex-wrap items-center gap-2 text-sm">
      {value && meta.data ? <a className="truncate underline" href={meta.data.url} target="_blank" rel="noreferrer">{meta.data.filename}</a> : value ? <span className="text-xs text-muted">…</span> : null}
      <button type="button" className="text-xs underline" disabled={up.isPending} onClick={() => ref.current?.click()}>{value ? t("app.change") : t("fields.upload")}</button>
      {value && <button type="button" className="text-xs text-muted underline" onClick={() => onChange("")}>{t("app.remove")}</button>}
      <input ref={ref} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) up.mutate(f); e.target.value = ""; }} />
      {error && <span className="text-xs text-danger">{error}</span>}
    </div>
  );
}

/** Inputs for every active custom field of a brand (palette entries flagged `custom`). Keys are stored without the `custom.` prefix. */
export function CustomFieldInputs({ brandId, entity, values, onChange, className }: Props) {
  const { i18n } = useTranslation();
  const palette = usePalette(brandId, entity);
  const defs = (palette.data?.fields ?? []).filter((x) => x.custom);
  if (defs.length === 0) return null;
  const set = (k: string, v: unknown) => onChange({ ...values, [k]: v });
  return (
    <div className={className ?? "rounded-lg bg-surface-2 p-2 grid grid-cols-2 gap-2"}>
      {defs.map((d) => { const k = d.field.slice(7); const v = values[k]; return (
        <Field key={d.field} label={tx(d.label, i18n.language)}>
          {d.type === "bool" ? <input type="checkbox" checked={!!v} onChange={(e) => set(k, e.target.checked)} />
            : d.type === "paragraph" ? <Textarea rows={3} value={String(v ?? "")} onChange={(e) => set(k, e.target.value)} />
            : d.type === "file" ? <FileField brandId={brandId} entity={entity === "customers" ? "customer" : "appointment"} value={v ? String(v) : ""} onChange={(id) => set(k, id)} />
            : d.type === "select" ? <Select value={String(v ?? "")} onChange={(e) => set(k, e.target.value)}><option value="">—</option>{d.options?.map((o) => <option key={o} value={o}>{o}</option>)}</Select>
            : <Input type={d.type === "number" ? "number" : d.type === "date" ? "date" : "text"} value={String(v ?? "")} onChange={(e) => set(k, e.target.value)} />}
        </Field>); })}
    </div>
  );
}
